import {
  ContentComment,
  Course,
  CourseCertificate,
  FilterCourse,
  Instructor,
  User,
  UserCourse
} from "domain/entities/entities";
import { interfaceCourseRepository } from "../../../infrastructure/repository/impl/course_repository";
import { ListResponse } from "../../entities/shop/product/listResponse";
import { Order } from "../../entities/order";

export interface CourseUseCases {
  get(courseID: number): Promise<Course>;

  list(page: number, filter?: FilterCourse): Promise<ListResponse<Course>>;

  listRecommendedCourses(): Promise<Course[]>;

  listByCourseTrack(
    order: Order,
    courseTrackID: number
  ): Promise<ListResponse<Course>>;

  enrollInCourse(courseID: number): Promise<unknown>;

  userCourseSituation(courseID: number): Promise<UserCourse>;

  finishCourse(courseID: number): Promise<void>;

  listNewCourses(): Promise<Course[]>;

  getCertificate(courseID: number): Promise<CourseCertificate>;

  listBySearch(order: Order): Promise<ListResponse<Course>>;

  getComments(
    courseId: number,
    order: Order
  ): Promise<ListResponse<ContentComment>>;

  getInstructors(courseID: number): Promise<Instructor[]>;

  leaveCourse(courseID: number): Promise<void>;

  indicationCourse(courseID: number): Promise<void>;

  listIndicationsCourses(): Promise<ListResponse<Course[]>>;

  loadIndicationByUsers(courseID: number): Promise<ListResponse<User[]>>;

  addFavoriteTraining(courseID: number): Promise<void>;

  removeFavoriteTraining(courseID: number): Promise<void>;

  listFavoriteTrainings(order: Order): Promise<ListResponse<Course[]>>;
}

class _CourseUseCases implements CourseUseCases {
  async get(courseID: number): Promise<Course> {
    return await interfaceCourseRepository.get(courseID);
  }

  async list(
    page: number,
    filter?: FilterCourse
  ): Promise<ListResponse<Course>> {
    return await interfaceCourseRepository.list(page, filter);
  }

  async listRecommendedCourses(): Promise<Course[]> {
    return await interfaceCourseRepository.listRecommendedCourses();
  }

  async listByCourseTrack(
    order: Order,
    courseTrackID: number
  ): Promise<ListResponse<Course>> {
    return await interfaceCourseRepository.listByCourseTrack(
      order,
      courseTrackID
    );
  }

  async enrollInCourse(courseID: number): Promise<unknown> {
    return await interfaceCourseRepository.enrollInCourse(courseID);
  }

  async userCourseSituation(courseID: number): Promise<UserCourse> {
    return await interfaceCourseRepository.userCourseSituation(courseID);
  }

  async finishCourse(courseID: number): Promise<void> {
    await interfaceCourseRepository.finishCourse(courseID);
  }

  async listNewCourses(): Promise<Course[]> {
    return await interfaceCourseRepository.listNewCourses();
  }

  getCertificate = async (courseID: number): Promise<CourseCertificate> => {
    return await interfaceCourseRepository.getCertificate(courseID);
  };

  async listBySearch(order: Order): Promise<ListResponse<Course>> {
    return await interfaceCourseRepository.listBySearch(order);
  }

  async getComments(
    courseId: number,
    order: Order
  ): Promise<ListResponse<ContentComment>> {
    return await interfaceCourseRepository.getComments(courseId, order);
  }

  async getInstructors(courseID: number): Promise<Instructor[]> {
    return await interfaceCourseRepository.getInstructors(courseID);
  }

  async leaveCourse(courseID: number): Promise<void> {
    return await interfaceCourseRepository.leaveCourse(courseID);
  }

  async indicationCourse(courseID: number): Promise<void> {
    return await interfaceCourseRepository.indicationCourse(courseID);
  }

  async listIndicationsCourses(): Promise<ListResponse<Course[]>> {
    return await interfaceCourseRepository.listIndicationsCourses();
  }

  async loadIndicationByUsers(courseID: number): Promise<ListResponse<User[]>> {
    return await interfaceCourseRepository.loadIndicationByUsers(courseID);
  }

  async addFavoriteTraining(courseID: number): Promise<void> {
    return await interfaceCourseRepository.addFavoriteTraining(courseID);
  }

  async removeFavoriteTraining(courseID: number): Promise<void> {
    return await interfaceCourseRepository.removeFavoriteTraining(courseID);
  }

  async listFavoriteTrainings(order: Order): Promise<ListResponse<Course[]>> {
    return await interfaceCourseRepository.listFavoriteTrainings(order);
  }
}

export const InterfaceCourseUseCases: CourseUseCases = new _CourseUseCases();
